import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Wallet, Plus, Trash2, Loader2, Building2, Pencil, RefreshCw, StopCircle } from 'lucide-react';

const EXPENSE_CATEGORIES = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'repairs', label: 'Repairs' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'business', label: 'Business' },
  { value: 'compliance', label: 'Compliance (Taxes & Gov Fees)' },
  { value: 'other', label: 'Other' },
];

export default function LandlordExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterProperty, setFilterProperty] = useState('all');
  const [processingRecurring, setProcessingRecurring] = useState(false);
  const [formData, setFormData] = useState({
    property_id: '',
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    is_recurring: false,
    recurring_end_date: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [expensesRes, propsRes] = await Promise.all([
        landlordAPI.getExpenses(),
        landlordAPI.getProperties(),
      ]);
      setExpenses(expensesRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredExpenses = filterProperty === 'all'
    ? expenses
    : expenses.filter(e => e.property_id === filterProperty);

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const recurringCount = expenses.filter(e => e.is_recurring).length;

  const handleOpenDialog = () => {
    setEditingExpense(null);
    setFormData({
      property_id: filterProperty !== 'all' ? filterProperty : '',
      category: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      is_recurring: false,
      recurring_end_date: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      property_id: expense.property_id || '',
      category: expense.category || '',
      description: expense.description || '',
      amount: expense.amount || '',
      date: expense.date?.split('T')[0] || '',
      is_recurring: expense.is_recurring || false,
      recurring_end_date: expense.recurring_end_date || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.category || !formData.description || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        property_id: formData.property_id || null,
        amount: parseFloat(formData.amount),
        recurring_end_date: formData.recurring_end_date || null,
      };
      if (editingExpense) {
        await landlordAPI.editExpense(editingExpense.expense_id, payload);
        toast.success('Expense updated');
      } else {
        await landlordAPI.createExpense(payload);
        toast.success('Expense recorded');
      }
      setDialogOpen(false);
      setEditingExpense(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await landlordAPI.deleteExpense(expenseId);
      toast.success('Expense deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleStopRecurring = async (expenseId) => {
    try {
      await landlordAPI.stopRecurringExpense(expenseId);
      toast.success('Recurring expense stopped');
      loadData();
    } catch (error) {
      toast.error('Failed to stop recurring expense');
    }
  };

  const handleProcessRecurring = async () => {
    setProcessingRecurring(true);
    try {
      const res = await landlordAPI.processRecurringExpenses();
      toast.success(res.data.message || 'Recurring expenses processed');
      loadData();
    } catch (error) {
      toast.error('Failed to process recurring expenses');
    } finally {
      setProcessingRecurring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Expenses</h1>
          <p className="text-muted-foreground mt-1">Track property expenses</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterProperty} onValueChange={setFilterProperty}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by property" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.property_id} value={prop.property_id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {recurringCount > 0 && (
            <Button
              variant="outline"
              onClick={handleProcessRecurring}
              disabled={processingRecurring}
              data-testid="process-recurring-btn"
            >
              {processingRecurring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Generate Monthly
            </Button>
          )}
          <Button onClick={handleOpenDialog} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-3xl font-bold text-[#C85A42]">{formatCurrency(totalExpenses)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#C85A42] flex items-center justify-center">
                <Wallet className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Recurring Expenses</p>
                <p className="text-3xl font-bold text-blue-600">{recurringCount}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses List */}
      {filteredExpenses.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Wallet className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No expenses recorded</h3>
            <p className="text-muted-foreground mb-4">Start tracking your property expenses</p>
            <Button onClick={handleOpenDialog} className="bg-[#C85A42] hover:bg-[#B04A35]">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Property</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((expense) => {
                    const prop = properties.find(p => p.property_id === expense.property_id);
                    return (
                      <tr key={expense.expense_id}>
                        <td>{formatDate(expense.date)}</td>
                        <td className="capitalize">{expense.category}</td>
                        <td>{expense.description}</td>
                        <td>
                          {prop ? (
                            <div className="flex items-center gap-1.5">
                              <Building2 className="w-4 h-4 text-muted-foreground" />
                              {prop.name}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">General</span>
                          )}
                        </td>
                        <td className="font-semibold">{formatCurrency(expense.amount)}</td>
                        <td>
                          {expense.is_recurring ? (
                            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                              <RefreshCw className="w-3 h-3 mr-1" /> Recurring
                            </Badge>
                          ) : expense.recurring_parent_id ? (
                            <Badge variant="outline" className="text-xs">Auto-generated</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">One-time</Badge>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(expense)} data-testid={`edit-expense-${expense.expense_id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {expense.is_recurring && (
                              <Button variant="ghost" size="sm" onClick={() => handleStopRecurring(expense.expense_id)} title="Stop recurring" data-testid={`stop-recurring-${expense.expense_id}`}>
                                <StopCircle className="w-4 h-4 text-orange-500" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.expense_id)} data-testid={`delete-expense-${expense.expense_id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property (Optional)</Label>
              <Select 
                value={formData.property_id || "general"} 
                onValueChange={(v) => setFormData({ ...formData, property_id: v === "general" ? "" : v })}
              >
                <SelectTrigger data-testid="expense-property-select">
                  <SelectValue placeholder="Select property or leave for general" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Expense</SelectItem>
                  {properties.map((prop) => (
                    <SelectItem key={prop.property_id} value={prop.property_id}>
                      {prop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Plumbing repair"
                data-testid="expense-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (P) *</Label>
                <Input
                  id="amount"
                  type="text"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
            </div>

            {/* Recurring Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Recurring Monthly</Label>
                <p className="text-xs text-muted-foreground">Auto-generate this expense every month</p>
              </div>
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_recurring: v }))}
                data-testid="expense-recurring-toggle"
              />
            </div>

            {formData.is_recurring && (
              <div className="space-y-2">
                <Label>End Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.recurring_end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, recurring_end_date: e.target.value }))}
                  placeholder="Leave empty for indefinite"
                  data-testid="expense-recurring-end-date"
                />
                <p className="text-xs text-muted-foreground">Leave empty to recur indefinitely. Click "Generate Monthly" to create entries.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="save-expense-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
